import React from 'react';
import { HeatmapRow } from '../../../utils/analytics/occupancyMetrics';
import { formatDateShort } from '../../../utils/dateUtils';
import { seqColor } from './chartTheme';
import './OccupancyHeatmap.css';

interface OccupancyHeatmapProps {
  rows: HeatmapRow[];
  /** Week-start dates in column order. */
  weekStarts: string[];
}

/**
 * CSS-grid heatmap: site rows × week columns, one-hue sequential ramp
 * (light = empty, dark = full). Blackout cells get a hatched overlay plus
 * a text hint in the tooltip so the state is never color-alone.
 */
const OccupancyHeatmap: React.FC<OccupancyHeatmapProps> = ({ rows, weekStarts }) => {
  if (rows.length === 0) {
    return <p className="occupancy-heatmap-empty">No sites to display.</p>;
  }
  return (
    <div className="occupancy-heatmap-wrap">
      <div
        className="occupancy-heatmap"
        style={{ gridTemplateColumns: `100px repeat(${weekStarts.length}, minmax(26px, 1fr))` }}
      >
        <div className="occupancy-heatmap-corner" />
        {weekStarts.map(week => (
          <div key={week} className="occupancy-heatmap-col-label">
            {formatDateShort(week + 'T12:00:00')}
          </div>
        ))}
        {rows.map(row => (
          <React.Fragment key={row.siteId}>
            <div className="occupancy-heatmap-row-label" title={row.label}>{row.label}</div>
            {row.cells.map(cell => (
              <div
                key={cell.weekStart}
                className={`occupancy-heatmap-cell ${cell.blackout ? 'occupancy-heatmap-cell--blackout' : ''}`}
                style={{ background: seqColor(cell.value) }}
                title={`${row.label} · week of ${formatDateShort(cell.weekStart + 'T12:00:00')}: ${Math.round(cell.value * 100)}% occupied${cell.blackout ? ' · blackout' : ''}`}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="occupancy-heatmap-scale">
        <span>0%</span>
        <span className="occupancy-heatmap-ramp" />
        <span>100%</span>
        <span className="occupancy-heatmap-blackout-key">▨ blackout</span>
      </div>
    </div>
  );
};

export default OccupancyHeatmap;
